import { useEffect, useRef, useState } from 'react';

import { AutocompletePrediction } from '@fhs/expo-maps';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { useLocale } from 'state/intl';

const LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS: number = 3;

function useLocationPredictions(
  location: string,
  {
    addressesOnly = false,
  }: {
    addressesOnly?: boolean;
  } = {}
) {
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const { region } = useLocale();
  const toast = useToast();
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [predictionsForLocation, setPredictionsForLocation] = useState('');
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService>();
  const toastShownRef = useRef(false);

  if (libraryLoaded && !autocompleteServiceRef.current) {
    if (window.google?.maps?.places?.AutocompleteService) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    } else if (!toastShownRef.current) {
      toastShownRef.current = true;

      toast.show({
        duration: 4000,
        text: 'Looks like something went wrong — mind giving the page a quick refresh?',
        variant: 'negative',
      });
    }
  }

  useEffect(() => {
    if (!location || location.length < LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS) {
      setPredictions(() => []);
      setPredictionsForLocation(location);
      return;
    }

    if (!libraryLoaded) {
      return;
    }

    if (!autocompleteServiceRef.current) {
      setPredictions(() => []);
      setPredictionsForLocation(location);
      return;
    }

    let cancelled = false;

    const request = {
      input: location,
      componentRestrictions: { country: region },
      ...(addressesOnly && { types: ['address'] }),
    };

    autocompleteServiceRef.current.getPlacePredictions(
      request,
      (results, status: google.maps.places.PlacesServiceStatus) => {
        if (cancelled) {
          return;
        }

        const mappedResults =
          status === window.google.maps.places.PlacesServiceStatus.OK && results?.length
            ? results.map(prediction => ({
                placeId: prediction.place_id,
                description: prediction.description,
                mainText: prediction.structured_formatting.main_text,
                secondaryText: prediction.structured_formatting.secondary_text,
              }))
            : [];
        setPredictions(() => mappedResults);
        setPredictionsForLocation(location);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [addressesOnly, libraryLoaded, location, region]);

  const isLoading =
    Boolean(location && location.length >= LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS) &&
    predictionsForLocation !== location;

  return { isLoading, predictions };
}

export default useLocationPredictions;
